import { useAppState, useAppActions, TAB_IDS } from '../../context/AppContext.jsx';
import Icon from '../common/Icon.jsx';

/**
 * BottomTabBar — the primary navigation surface of the app.
 *
 * Design notes:
 *   - Three tabs only — the FOM product council reserved a fourth slot for
 *     "Live" once the broadcast integration ships. Until then, three.
 *   - The active tab is indicated by:
 *       1. F1-red icon + label
 *       2. A short race-stripe accent below the icon
 *       3. A subtle glow on the icon
 *   - Tap targets are 64×56 minimum (Apple HIG = 44, but we want generous
 *     thumb landing zones in single-handed use)
 *   - The bar respects bottom safe-area (home indicator) via padding
 */

const TAB_META = {
  news: { label: 'News', icon: 'news' },
  results: { label: 'Results', icon: 'flag' },
  oracle: { label: 'Oracle', icon: 'oracle' },
};

export default function BottomTabBar() {
  const { activeTab } = useAppState();
  const { setActiveTab } = useAppActions();

  return (
    <nav
      aria-label="Primary"
      className="relative z-30 shrink-0 border-t border-stroke/70 bg-bg-elevated/95 shadow-tab-bar backdrop-blur-md"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="grid grid-cols-3">
        {TAB_IDS.map((id) => {
          const meta = TAB_META[id];
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="pressable no-select group relative flex h-16 flex-col items-center justify-center gap-1 px-2"
              aria-current={isActive ? 'page' : undefined}
              aria-label={`Open ${meta.label}`}
            >
              <span
                className={`flex h-7 w-7 items-center justify-center transition-colors ${
                  isActive
                    ? 'text-f1-red drop-shadow-[0_0_8px_rgba(225,6,0,0.55)]'
                    : 'text-ink-tertiary group-hover:text-ink-secondary'
                }`}
              >
                <Icon name={meta.icon} size={22} strokeWidth={isActive ? 2 : 1.6} />
              </span>
              <span
                className={`text-2xs font-semibold tracking-wide transition-colors ${
                  isActive ? 'text-f1-red' : 'text-ink-tertiary'
                }`}
              >
                {meta.label}
              </span>
              <span
                aria-hidden="true"
                className={`absolute inset-x-6 -top-px h-[2px] rounded-b-full transition-all ${
                  isActive ? 'race-stripe opacity-100' : 'opacity-0'
                }`}
              />
            </button>
          );
        })}
      </div>
    </nav>
  );
}
